<template>
    <div v-if="isOpenProject" id="portfolio-project-details" class="portfolio-project-details-component"
         role="dialog" aria-modal="true" :aria-label="projectName"
         @click.self="openCloseProjectLocally">
        <div class="container">
            <button ref="closeButton" class="close-button" type="button" aria-label="Close project details"
                    @click="openCloseProjectLocally">
                <font-awesome-icon :icon="['fas', 'times']"/>
            </button>
            <header class="main-title title">{{projectName}}</header>
            <div class="details">
                <div class="gallery">
                    <div class="image-frame">
                        <img v-if="currentImage" class="project-image" :alt="projectName"
                             :src="getImgPath(currentImage)">
                        <template v-if="images.length > 1">
                            <button class="gallery-arrow prev" type="button" aria-label="Previous screenshot"
                                    @click="changeImage(-1)">
                                <font-awesome-icon :icon="['fas', 'chevron-circle-right']" flip="horizontal"/>
                            </button>
                            <button class="gallery-arrow next" type="button" aria-label="Next screenshot"
                                    @click="changeImage(1)">
                                <font-awesome-icon :icon="['fas', 'chevron-circle-right']"/>
                            </button>
                        </template>
                    </div>
                    <div v-if="images.length > 1" class="gallery-dots">
                        <button v-for="(image, index) in images" :key="image" type="button"
                                class="dot" :class="{active: index === imageIndex}"
                                :aria-label="'Screenshot ' + (index + 1)" @click="imageIndex = index"></button>
                    </div>
                </div>
                <div class="info">
                    <div class="project-description" v-html="projectDescription"></div>
                    <dl class="meta">
                        <div v-if="projectRole" class="meta-row">
                            <dt>Role</dt>
                            <dd>{{projectRole}}</dd>
                        </div>
                        <div v-if="projectDate" class="meta-row">
                            <dt>Period</dt>
                            <dd>{{projectDate}}</dd>
                        </div>
                    </dl>
                    <a v-if="projectSite" :href="projectSite" class="visit" target="_blank" rel="noopener">Visit site</a>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapState, mapActions} from 'vuex';

    export default {
        name: "PortfolioProjectDetails",
        data() {
            return {
                imageIndex: 0
            };
        },
        computed: {
            ...mapState({
                isOpenProject: state => state.portfolioProjectDetails.isOpenProject,
                projectName: state => state.portfolioProjectDetails.openPortfolioProject.name,
                projectSite: state => state.portfolioProjectDetails.openPortfolioProject.site,
                projectImage: state => state.portfolioProjectDetails.openPortfolioProject.image,
                projectImages: state => state.portfolioProjectDetails.openPortfolioProject.images,
                projectDescription: state => state.portfolioProjectDetails.openPortfolioProject.description,
                projectRole: state => state.portfolioProjectDetails.openPortfolioProject.role,
                projectDate: state => state.portfolioProjectDetails.openPortfolioProject.date
            }),
            images() {
                if (this.projectImages && this.projectImages.length) {
                    return this.projectImages;
                }
                return this.projectImage ? [this.projectImage] : [];
            },
            currentImage() {
                return this.images[this.imageIndex];
            }
        },
        watch: {
            projectName() {
                this.imageIndex = 0;
            },
            isOpenProject(isOpen) {
                if (isOpen) {
                    this.$nextTick(() => {
                        if (this.$refs.closeButton) {
                            this.$refs.closeButton.focus();
                        }
                    });
                }
            }
        },
        mounted() {
            document.addEventListener('keydown', this.onKeydown);
        },
        beforeDestroy() {
            document.removeEventListener('keydown', this.onKeydown);
        },
        methods: {
            ...mapActions('main', [
                'isShowBackdropAction',
            ]),
            ...mapActions('portfolioProjectDetails', [
                'openCloseProject',
            ]),
            openCloseProjectLocally() {
                this.openCloseProject();
                this.isShowBackdropAction();
            },
            onKeydown(event) {
                if (!this.isOpenProject) {
                    return;
                }
                if (event.key === 'Escape') {
                    this.openCloseProjectLocally();
                } else if (event.key === 'ArrowLeft' && this.images.length > 1) {
                    this.changeImage(-1);
                } else if (event.key === 'ArrowRight' && this.images.length > 1) {
                    this.changeImage(1);
                }
            },
            changeImage(step) {
                const count = this.images.length;
                this.imageIndex = (this.imageIndex + step + count) % count;
            },
            getImgPath(image) {
                return require(`@/assets/portfolio/` + image);
            },
        },
    }
</script>

<style lang="scss" scoped>
    #portfolio-project-details.portfolio-project-details-component {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        padding: 0;
        z-index: 1100;
        display: flex;
        align-items: center;
        justify-content: center;

        .container {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            width: min(1000px, 92%);
            max-height: 90%;
            padding: 36px 36px 32px;
            overflow: auto;
            background-color: #fff;
            border: 2px solid #e9ecef;
            border-radius: 12px;
            box-shadow: 0 20px 50px -12px rgba(44, 62, 80, .5);

            .close-button {
                position: absolute;
                top: 14px;
                right: 14px;
                width: 44px;
                height: 44px;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                font-size: 1.2em;
                color: #2c3e50;
                background-color: #fff;
                border: 2px solid #e9ecef;
                border-radius: 50%;
                cursor: pointer;
                transition: background-color .2s ease, border-color .2s ease;

                &:hover, &:focus-visible {
                    background-color: #e9ecef;
                    border-color: #2c3e50;
                    outline: none;
                }
            }

            .title {
                box-sizing: border-box;
                width: 100%;
                margin: 0 0 24px;
                padding: 0 56px;
                text-align: center;
            }

            .details {
                display: grid;
                grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr);
                gap: 32px;
                width: 100%;
                align-items: start;

                .gallery {
                    .image-frame {
                        position: relative;
                        overflow: hidden;
                        border: 2px solid #e9ecef;
                        border-radius: 8px;
                        background-color: #e9ecef;

                        .project-image {
                            display: block;
                            width: 100%;
                            height: auto;
                        }

                        .gallery-arrow {
                            position: absolute;
                            top: 50%;
                            width: 40px;
                            height: 40px;
                            padding: 0;
                            font-size: 1.6em;
                            line-height: 1;
                            color: #fff;
                            background: rgba(44, 62, 80, .7);
                            border: none;
                            border-radius: 50%;
                            transform: translateY(-50%);
                            cursor: pointer;

                            &.prev {
                                left: 10px;
                            }

                            &.next {
                                right: 10px;
                            }

                            &:hover, &:focus-visible {
                                background: #2c3e50;
                                outline: none;
                            }
                        }
                    }

                    .gallery-dots {
                        display: flex;
                        justify-content: center;
                        gap: 8px;
                        margin-top: 12px;

                        .dot {
                            width: 10px;
                            height: 10px;
                            padding: 0;
                            background: #e9ecef;
                            border: 1px solid #c9d1d9;
                            border-radius: 50%;
                            cursor: pointer;

                            &.active {
                                background: #2c3e50;
                                border-color: #2c3e50;
                            }
                        }
                    }
                }

                .info {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 18px;

                    .project-description {
                        font-size: 1.1em;
                        line-height: 1.6;
                    }

                    .meta {
                        width: 100%;
                        margin: 0;
                        border-top: 2px solid #e9ecef;

                        .meta-row {
                            display: flex;
                            gap: 16px;
                            padding: 10px 0;
                            border-bottom: 2px solid #e9ecef;

                            dt {
                                flex: 0 0 80px;
                                font-weight: bold;
                                color: #2c3e50;
                            }

                            dd {
                                margin: 0;
                                color: #555;
                            }
                        }
                    }


                    .visit {
                        padding: 10px 22px;
                        color: #fff;
                        text-decoration: none;
                        background-color: #2c3e50;
                        border: 2px solid #2c3e50;
                        border-radius: 999px;
                        transition: background-color .2s ease, color .2s ease;

                        &:hover, &:focus-visible {
                            color: #2c3e50;
                            background-color: #fff;
                            outline: none;
                        }
                    }
                }
            }
        }
    }

    /*media*/
    @media screen and (max-width: 900px) {
        #portfolio-project-details.portfolio-project-details-component {
            .container {
                padding: 28px 18px 22px;

                .details {
                    grid-template-columns: minmax(0, 1fr);
                    gap: 20px;
                }
            }
        }
    }
</style>
